import { Injectable, inject, DOCUMENT } from '@angular/core';

import { ENV } from '@bodyanalytics/app-config';

export interface TenantBrandingTokensI {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logo: string | null;
  favicon: string | null;
  fontDisplay: string;
  fontBody: string;
  portalTitle: string | null;
}

/** Tenant identity resolved by the public by-slug branding endpoint. */
export interface TenantBrandingContextI {
  tenantId: string;
  slug: string;
}

const DEFAULTS: TenantBrandingTokensI = {
  primaryColor: '#0A3D5C',
  secondaryColor: '#ffffff',
  accentColor: '#D4860A',
  logo: null,
  favicon: null,
  fontDisplay: 'Fraunces',
  fontBody: 'Plus Jakarta Sans',
  portalTitle: null,
};

/**
 * Render-boundary allowlists (issue #965).
 *
 * This service is the point where tenant-controlled branding values are
 * written into the live DOM: CSS custom properties on <html>, the document
 * title, and a favicon <link href>. Even though the API now validates branding
 * on write, this layer must NOT trust the values it receives — a branding row
 * written before the server-side fix, or fetched via a path that bypasses the
 * validated PUT endpoint, could still contain a payload like
 * `expression(alert(1))`, `url(javascript:...)`, `</style>...`, or a
 * `javascript:` favicon URL.
 *
 * Strategy: validate every token against the shape we expect and fall back to
 * the design-system default if it does not match. We never interpolate a raw
 * branding string into a style/href. setProperty already uses the CSSOM (no
 * <style>-block string concatenation), so the only residual risk is a malicious
 * *value*; the regex allowlists below close that.
 */
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const ALLOWED_FONTS = ['Fraunces', 'Plus Jakarta Sans', 'Geist Mono'];

/**
 * Shared runtime-theming service — the SINGLE implementation for every portal
 * (formerly duplicated in `apps/front/admin/src/app/branding.service.ts`).
 *
 * Two entry points, both called from an app initializer:
 *   - `init(tenantId)`   — admin portal: the tenant is known at build time
 *                          (`environment.companyId`).
 *   - `initBySlug(slug)` — calendar portal: the tenant is resolved at RUNTIME
 *                          from the `/t/:slug` URL prefix; returns the resolved
 *                          `{ tenantId, slug }` so the caller can seed its
 *                          tenant-context store, or `null` on any failure
 *                          (unknown slug, network error) — in which case the
 *                          design-system defaults are applied and the app
 *                          boots exactly like the unbranded portal.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly env = inject<{ api: string }>(ENV);
  private readonly document = inject(DOCUMENT);

  /** Called by APP_INITIALIZER — resolves before first render.
   *  Uses native fetch (not HttpClient) to avoid deadlocking with the
   *  auth interceptor, which waits for Auth0 isLoading$ before it can
   *  attach a token — a circular dependency on first load. */
  async init(tenantId: string): Promise<void> {
    try {
      const response = await fetch(`${this.env.api}/tenants/${tenantId}/branding`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const branding = (await response.json()) as Partial<TenantBrandingTokensI>;
      this.applyTokens(branding);
    } catch (_err: unknown) {
      // Fallback to design-system defaults; never block app init
      this.applyTokens(DEFAULTS);
    }
  }

  /** Slug variant for runtime multi-tenancy (`GET /tenants/by-slug/:slug/branding`).
   *  Same native-fetch/no-token posture as `init`. Never throws and never
   *  blocks bootstrap: any failure applies defaults and resolves `null`. */
  async initBySlug(slug: string): Promise<TenantBrandingContextI | null> {
    try {
      const response = await fetch(`${this.env.api}/tenants/by-slug/${encodeURIComponent(slug)}/branding`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = (await response.json()) as Partial<TenantBrandingTokensI> & {
        tenantId?: unknown;
        slug?: unknown;
      };
      this.applyTokens(payload);
      return typeof payload.tenantId === 'string' && payload.tenantId !== ''
        ? { tenantId: payload.tenantId, slug }
        : null;
    } catch (_err: unknown) {
      this.applyTokens(DEFAULTS);
      return null;
    }
  }

  private applyTokens(tokens: Partial<TenantBrandingTokensI>): void {
    const safe = this.sanitizeTokens(tokens);
    const root = this.document.documentElement;

    root.style.setProperty('--color-primary', safe.primaryColor);
    root.style.setProperty('--color-secondary', safe.secondaryColor);
    root.style.setProperty('--color-accent', safe.accentColor);
    root.style.setProperty('--font-display', `'${safe.fontDisplay}', serif`);
    root.style.setProperty('--font-body', `'${safe.fontBody}', sans-serif`);

    if (safe.portalTitle) {
      // textContent path — never bound via innerHTML, so HTML is inert. The
      // sanitizer also strips angle brackets defensively.
      this.document.title = safe.portalTitle;
    }

    if (safe.favicon) {
      const link: HTMLLinkElement =
        (this.document.querySelector("link[rel*='icon']") as HTMLLinkElement) ?? this.document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = safe.favicon;
      this.document.head.appendChild(link);
    }
  }

  /**
   * Coerce arbitrary (possibly hostile) branding values into the safe shapes
   * the renderer expects. Anything that fails validation is replaced with the
   * design-system default rather than rendered.
   */
  private sanitizeTokens(tokens: Partial<TenantBrandingTokensI>): TenantBrandingTokensI {
    return {
      primaryColor: this.safeColor(tokens.primaryColor, DEFAULTS.primaryColor),
      secondaryColor: this.safeColor(tokens.secondaryColor, DEFAULTS.secondaryColor),
      accentColor: this.safeColor(tokens.accentColor, DEFAULTS.accentColor),
      logo: this.safeHttpsUrl(tokens.logo),
      favicon: this.safeHttpsUrl(tokens.favicon),
      fontDisplay: this.safeFont(tokens.fontDisplay, DEFAULTS.fontDisplay),
      fontBody: this.safeFont(tokens.fontBody, DEFAULTS.fontBody),
      portalTitle: this.safeTitle(tokens.portalTitle),
    };
  }

  private safeColor(value: string | null | undefined, fallback: string): string {
    return typeof value === 'string' && HEX_COLOR_REGEX.test(value.trim()) ? value.trim() : fallback;
  }

  private safeFont(value: string | null | undefined, fallback: string): string {
    return typeof value === 'string' && ALLOWED_FONTS.includes(value.trim()) ? value.trim() : fallback;
  }

  /** Only https URLs are allowed for asset references — blocks `javascript:`,
   *  `data:`, and any other script-capable scheme. */
  private safeHttpsUrl(value: string | null | undefined): string | null {
    if (typeof value !== 'string' || value.trim() === '') return null;
    try {
      const url = new URL(value.trim());
      return url.protocol === 'https:' ? url.href : null;
    } catch {
      return null;
    }
  }

  /** Plain text only — strip angle brackets so a stored payload cannot become
   *  HTML even if a future caller mistakenly binds the title via innerHTML. */
  private safeTitle(value: string | null | undefined): string | null {
    if (typeof value !== 'string') return null;
    const cleaned = value.replace(/[<>]/g, '').trim().slice(0, 120);
    return cleaned === '' ? null : cleaned;
  }
}
